/*
 * ____   ____    .__   __
 * \   \ /   /___ |  |_/  |______      ____ ___.__.
 *  \   Y   /  _ \|  |\   __  \_ \   / ___<   |  |
 *   \     (  <_> )  |_|  |  / __ \_/ /_/  >___  |
 *    \___/ \____/|____/__| (____  /\___  // ____|
 *                               \//_____/ \/
 *
 * Copyright (c) 2017-2025 Voltagy. All rights reserved.
 * https://voltagy.com
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the proprietary and confidential property of Voltagy.
 * Unauthorized copying, transfer, or use of this file, via any medium, is
 * strictly prohibited without the express written permission of Voltagy.
 *
 * The receipt or possession of this source code and/or related information
 * does not convey or imply any right to use it, reproduce it, disclose its
 * contents, or to manufacture, use, or sell anything that it may describe.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE
 * ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE
 * LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR
 * CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF
 * SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN
 * CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 * ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 *
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "react-toastify";

import { useAuth } from "../hooks/useAuth";
import {
  isTokenExpiringSoon,
  handleTokenRefresh,
} from "../services/authService";
import { api as VoltagyAPI } from "../services/VoltagyAPI";
import { offlineQueue } from "../utils/OfflineQueue";
import SocketClient from "../utils/SocketClient";

const SessionManagement = () => {
  const { isAuthenticated, forceTokenRefresh, logout, updateLastActivity } =
    useAuth();
  const [isRecovering, setIsRecovering] = useState(false);
  const lastPingTimestamp = useRef(Date.now());
  const pingIntervalRef = useRef(null);
  const recoveryTimeoutRef = useRef(null);
  const visibilityChangeHandlerRef = useRef(null);
  const networkStatusRef = useRef(navigator.onLine);

  const processOfflineQueue = useCallback(async () => {
    try {
      if (offlineQueue.length === 0) return;

      toast({
        title: "Connection Restored",
        description: "Processing offline changes...",
        status: "info",
        duration: 3000,
        isClosable: true,
      });

      await offlineQueue.processQueue();

      toast({
        title: "Sync Complete",
        description: "All offline changes have been processed.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error processing offline queue:", error);

      toast({
        title: "Sync Failed",
        description:
          "Failed to process some offline changes. Will retry later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, []);

  const updateConnectionStatus = useCallback(
    (isConnected) => {
      if (!networkStatusRef.current && isConnected) {
        console.log("Connection restored, processing offline queue...");
        processOfflineQueue();
      } else if (networkStatusRef.current && !isConnected) {
        console.log("Connection lost");

        toast({
          title: "Connection Lost",
          description:
            "Working in offline mode. Changes will be saved when you reconnect.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }

      networkStatusRef.current = isConnected;
    },
    [processOfflineQueue]
  );

  const validateToken = useCallback(async () => {
    if (!isAuthenticated) return false;

    try {
      if (updateLastActivity) {
        updateLastActivity();
      }

      if (isTokenExpiringSoon()) {
        console.log("Token expiring soon, forcing refresh...");
        const result = await handleTokenRefresh();
        return !!result && result.success;
      }
      return true;
    } catch (error) {
      console.error("Error validating token:", error);
      return false;
    }
  }, [isAuthenticated, updateLastActivity]);

  const handleNetworkOnline = useCallback(() => {
    console.log("Network is online");

    setTimeout(() => {
      if (isAuthenticated) {
        validateToken()
          .then((valid) => {
            if (valid) {
              SocketClient.reconnectIfNeeded()
                .then(() => {
                  updateConnectionStatus(true);
                })
                .catch((error) => {
                  console.error(
                    "Error reconnecting socket after network online:",
                    error
                  );
                });
            } else {
              console.warn("Token validation failed after network online");
            }
          })
          .catch((error) => {
            console.error(
              "Error validating token after network online:",
              error
            );
          });
      }
    }, 2000);
  }, [isAuthenticated, validateToken, updateConnectionStatus]);

  const handleNetworkOffline = useCallback(() => {
    console.log("Network is offline");
    updateConnectionStatus(false);
  }, [updateConnectionStatus]);

  const attemptSessionRecovery = useCallback(async () => {
    if (isRecovering) return;

    try {
      setIsRecovering(true);
      console.log("Attempting session recovery...");

      let tokenRefreshed = false;
      try {
        const refreshResult = await forceTokenRefresh();
        tokenRefreshed = !!refreshResult;
        if (!tokenRefreshed) {
          console.warn("Session recovery: Token refresh failed");
        }
      } catch (error) {
        console.error("Error refreshing token during recovery:", error);
      }

      let networkRestored = false;
      if (SocketClient) {
        const socketReconnected = await SocketClient.reconnectIfNeeded();
        if (!socketReconnected) {
          console.warn("Session recovery: Socket reconnection failed");
        } else {
          networkRestored = true;
        }
      }

      try {
        const healthResponse = await VoltagyAPI.get("/health/short");
        if (!healthResponse.data || !healthResponse.data.status === "OK") {
          throw new Error("Health API responded with NOT OK");
        }

        networkRestored = true;
        console.log("Session recovery successful");
        lastPingTimestamp.current = Date.now();

        if (networkRestored) {
          console.log("Network restored, processing offline queue...");
          await processOfflineQueue();
        }

        toast({
          title: "Connection Restored",
          description: "Your session has been successfully recovered.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error("Session recovery failed after attempts:", error);

        toast({
          title: "Connection Issues",
          description:
            "Having trouble connecting to the server. Would you like to refresh the page?",
          status: "error",
          duration: 10000,
          isClosable: true,
          action: () => {
            if (
              window.confirm("Would you like to refresh the page to reconnect?")
            ) {
              window.location.reload();
            }
          },
        });
      }
    } finally {
      setIsRecovering(false);
    }
  }, [isRecovering, forceTokenRefresh, processOfflineQueue]);

  const checkAppHealth = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      if (isRecovering) return;

      const currentTime = Date.now();
      const timeSinceLastPing = currentTime - lastPingTimestamp.current;

      if (timeSinceLastPing > 5 * 60 * 1000) {
        // 1 minute
        console.log("Performing app health check after inactivity...");

        // P1: Authorized
        const tokenResult = await validateToken();
        if (!tokenResult) {
          console.warn("Health check: Token validation failed");
        }

        // P2: socket connection
        const socketConnected = SocketClient.isConnected();
        if (!socketConnected) {
          console.warn("Health check: Socket disconnected");
          await SocketClient.reconnectIfNeeded();
        }

        // P3: lightweight API request
        try {
          const healthResponse = await VoltagyAPI.get("/health/short");
          console.log(healthResponse.data);
          if (!healthResponse.data || !healthResponse.data.status === "OK") {
            throw new Error("Health API responded with NOT OK");
          }
        } catch (error) {
          console.warn("Health check: API test failed:", error);
          throw error;
        }

        lastPingTimestamp.current = currentTime;
      }

      console.log("Health check passed successfully");
    } catch (error) {
      console.error("Health check failed:", error);

      if (isAuthenticated && !isRecovering) {
        attemptSessionRecovery();
      }
    }
  }, [isAuthenticated, isRecovering, validateToken, attemptSessionRecovery]);

  const handleVisibilityChange = useCallback(() => {
    if (!document.hidden && isAuthenticated) {
      console.log("Page became visible, checking application health...");
      lastPingTimestamp.current = Date.now();

      if (updateLastActivity) {
        updateLastActivity();
      }

      setTimeout(() => {
        checkAppHealth();
      }, 1000); // 1 second
    }
  }, [isAuthenticated, updateLastActivity, checkAppHealth]);

  const reportUserActivity = useCallback(() => {
    if (isAuthenticated && SocketClient) {
      lastPingTimestamp.current = Date.now();
      SocketClient.reportUserActivity();

      if (updateLastActivity) {
        updateLastActivity();
      }
    }
  }, [isAuthenticated, updateLastActivity]);

  const handleUserInteraction = useCallback(() => {
    reportUserActivity();
  }, [reportUserActivity]);

  useEffect(() => {
    if (isAuthenticated) {
      SocketClient.setConnectionStatusCallback(updateConnectionStatus);

      window.addEventListener("online", handleNetworkOnline);
      window.addEventListener("offline", handleNetworkOffline);

      pingIntervalRef.current = setInterval(() => {
        if (!document.hidden && updateLastActivity) {
          lastPingTimestamp.current = Date.now();
          updateLastActivity();
        }
      }, 30 * 60 * 1000); // 1 minute

      recoveryTimeoutRef.current = setInterval(() => {
        if (!document.hidden) {
          checkAppHealth();
        }
      }, 2 * 60 * 1000); // 2 minutes

      visibilityChangeHandlerRef.current = handleVisibilityChange;
      document.addEventListener(
        "visibilitychange",
        visibilityChangeHandlerRef.current
      );

      lastPingTimestamp.current = Date.now();

      offlineQueue.loadQueue();

      if (!navigator.onLine) {
        updateConnectionStatus(false);
      }
    }

    pingIntervalRef.current = setInterval(() => {
      if (!document.hidden) {
        reportUserActivity();
      }
    }, 30 * 60 * 1000); // 1 minute

    const events = ["mousedown", "keypress", "scroll", "touchstart"];

    let lastReportTime = 0;
    const throttledHandler = () => {
      const now = Date.now();
      if (now - lastReportTime > 10000) {
        lastReportTime = now;
        handleUserInteraction();
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, throttledHandler, { passive: true });
    });

    reportUserActivity();

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      if (recoveryTimeoutRef.current) {
        clearInterval(recoveryTimeoutRef.current);
      }

      if (visibilityChangeHandlerRef.current) {
        document.removeEventListener(
          "visibilitychange",
          visibilityChangeHandlerRef.current
        );
      }

      SocketClient.removeConnectionStatusCallback(updateConnectionStatus);

      window.removeEventListener("online", handleNetworkOnline);
      window.removeEventListener("offline", handleNetworkOffline);

      events.forEach((event) => {
        window.removeEventListener(event, throttledHandler);
      });
    };
  }, [
    isAuthenticated,
    updateConnectionStatus,
    checkAppHealth,
    handleVisibilityChange,
    handleNetworkOnline,
    handleNetworkOffline,
    updateLastActivity,
    reportUserActivity,
    handleUserInteraction,
  ]);

  return null;
};

export default SessionManagement;
